import { useEffect, useMemo, useRef } from "react";
import { getTagMeasurementsLast7Days } from "@/api/measurement-api";
import { calculateAverageAndZScore } from "@/lib/tag-data-utils.js";
import { removeCommasAndSpaces } from "@/lib/formatting-utils.js";
import { useTagDataContext } from "@/contexts/TagDataContext.jsx";

/**
 * Custom hook to fetch and cache trend column data (trend, average, z-score)
 * Uses TagDataContext to cache data across page navigation
 *
 * @param {Array} allParameters - Array of all tag parameters
 * @param {Function} getParameterId - Function to get unique ID for a parameter
 * @param {string} assetId - The asset ID to cache data for
 * @returns {Object} { trendColumns, trendColumnRawData, isLoadingTrendColumns }
 */
export function useTrendColumns(allParameters, getParameterId, assetId) {
  const { getTrendColumns, setTrendColumns } = useTagDataContext();

  // Get cached trend columns from context
  const { trendColumns, trendColumnRawData, trendColumnsCache, isLoading } =
    getTrendColumns(assetId || "");

  // Track which parameters we've already fetched to avoid duplicate requests
  const fetchedParamsRef = useRef(new Set());
  const isFetchingRef = useRef(false);
  const lastAssetIdRef = useRef(assetId);

  // Reset tracking when asset changes
  useEffect(() => {
    if (lastAssetIdRef.current !== assetId) {
      fetchedParamsRef.current.clear();
      isFetchingRef.current = false;
      lastAssetIdRef.current = assetId;
    }
  }, [assetId]);

  // Extract parameter IDs that need data
  const parameterIds = useMemo(() => {
    if (!allParameters?.length) return [];

    const ids = new Set();
    allParameters.forEach((param) => {
      if (param.apiData?.assetTagId && param.apiData?.assetId) {
        const paramId = getParameterId(param);
        if (paramId) ids.add(paramId);
      }
    });
    return Array.from(ids).sort(); // Sorted for stable reference
  }, [allParameters, getParameterId]);

  // Fetch missing trend column data
  useEffect(() => {
    if (!assetId || !parameterIds.length || isFetchingRef.current) {
      return;
    }

    // Get current cache state
    const cacheState = getTrendColumns(assetId);
    const cachedTrendColumnsMap = cacheState.trendColumnsCache;

    // Find parameters that need fetching
    const missingParamIds = parameterIds.filter(
      (paramId) =>
        !cachedTrendColumnsMap.has(paramId)
        && !fetchedParamsRef.current.has(paramId)
    );

    if (missingParamIds.length === 0) {
      return;
    }

    // Mark as fetching and track which params we're fetching
    isFetchingRef.current = true;
    missingParamIds.forEach((id) => fetchedParamsRef.current.add(id));

    // Set loading state
    if (!cacheState.isLoading) {
      setTrendColumns(assetId, cachedTrendColumnsMap, true);
    }

    // Fetch data for all missing parameters
    const fetchData = async () => {
      try {
        // Get fresh cache state
        const currentCache = getTrendColumns(assetId);
        const trendColumnsCache = new Map(currentCache.trendColumnsCache);

        // Find parameter objects for the IDs we need to fetch
        const paramsToFetch = allParameters.filter((param) => {
          const paramId = getParameterId(param);
          return paramId && missingParamIds.includes(paramId);
        });

        // Fetch data for each parameter
        const fetchPromises = paramsToFetch.map(async (param) => {
          const paramId = getParameterId(param);
          if (!paramId) return;

          try {
            const apiResponse = await getTagMeasurementsLast7Days(
              param.apiData.assetTagId,
              param.apiData.assetId
            );

            if (
              apiResponse?.Measurements
              && Array.isArray(apiResponse.Measurements)
            ) {
              // Calculate stats
              const currentValue = parseFloat(
                removeCommasAndSpaces(param.currentValue)
              );
              const numericValue =
                !isNaN(currentValue) && isFinite(currentValue)
                  ? currentValue
                  : null;

              const { average, zScore } = calculateAverageAndZScore(
                apiResponse.Measurements,
                numericValue
              );

              trendColumnsCache.set(paramId, {
                rawData: apiResponse,
                stats: { average, zScore },
              });
            } else {
              trendColumnsCache.set(paramId, {
                rawData: null,
                stats: { average: null, zScore: null },
              });
            }
          } catch (err) {
            console.error(
              `Error fetching trend column data for ${param.parameter}:`,
              err
            );
            trendColumnsCache.set(paramId, {
              rawData: null,
              stats: { average: null, zScore: null },
            });
          }
        });

        await Promise.all(fetchPromises);

        // Update cache with fetched data
        setTrendColumns(assetId, trendColumnsCache, false);
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterIds, assetId, setTrendColumns]);

  return {
    trendColumns,
    trendColumnRawData,
    isLoadingTrendColumns: isLoading,
  };
}
